import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AppShell from '../components/AppShell';
import { useUnits } from '../lib/units';
import { apiFetch, apiFetchBlob } from '../lib/api';
import { fmtAccounting, fmtDate, fmtMoney } from '../lib/format';
import type { StatementResponse, StatementLine } from '../lib/types';

type Preset = 'last30' | 'last90' | 'ytd' | 'all';

export default function Statement() {
    const { t, i18n } = useTranslation();
    const { active, activeCntId } = useUnits();

    const [preset, setPreset] = useState<Preset>('last90');
    const [data, setData] = useState<StatementResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    const [downloading, setDownloading] = useState(false);

    useEffect(() => {
        if (!activeCntId) return;
        let cancelled = false;
        setLoading(true); setErr(null);
        apiFetch<StatementResponse>(`/api/account/statement?cntId=${activeCntId}&preset=${preset}`)
            .then((r) => { if (!cancelled) setData(r); })
            .catch((e) => { if (!cancelled) setErr((e as Error).message); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [activeCntId, preset]);

    async function downloadPdf() {
        if (!activeCntId) return;
        setDownloading(true);
        try {
            const { blob, filename } = await apiFetchBlob(
                `/api/account/statement.pdf?cntId=${activeCntId}&preset=${preset}&lng=${i18n.language === 'ar' ? 'ar' : 'en'}`,
            );
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename || `statement-${activeCntId}-${preset}.pdf`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
        } finally { setDownloading(false); }
    }

    // Group lines by currency. Seed the groups from `data.opening` too, so a
    // multi-currency client with zero lines in the period still sees one block
    // per currency rendered (opening + "no activity" + closing).
    const groups = useMemo(() => {
        const m = new Map<number | null, StatementLine[]>();
        for (const o of data?.opening ?? []) if (!m.has(o.crncID)) m.set(o.crncID, []);
        for (const l of data?.lines   ?? []) {
            if (!m.has(l.crncID)) m.set(l.crncID, []);
            m.get(l.crncID)!.push(l);
        }
        return m;
    }, [data?.lines, data?.opening]);

    return (
        <AppShell>
            <div className="max-w-[1280px] mx-auto px-4 sm:px-12 py-8 sm:py-12">
                <header className="mb-8 sm:mb-10 stagger">
                    <p className="eyebrow text-ember">{t('nav.statement')}</p>
                    <h1 className="font-display text-ink-on text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-2 text-balance">
                        {t('statement.title')}
                    </h1>
                    {active && (
                        <p className="font-body text-ink-muted text-sm mt-2">
                            {active.bldNm} — {active.untNm}
                            {data?.range?.from && (
                                <span className="text-ink-dim block sm:inline sm:ms-3 mt-1 sm:mt-0">
                                    {fmtDate(data.range.from, i18n.language)} → {fmtDate(data.range.to, i18n.language)}
                                </span>
                            )}
                        </p>
                    )}

                    {/* Preset tabs + Download PDF — stack on mobile, side-by-side on sm+ */}
                    <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <PresetGroup current={preset} onChange={setPreset} />
                        <button onClick={downloadPdf} disabled={downloading || loading || !data}
                                className="btn btn-primary w-full sm:w-auto">
                            {downloading ? t('common.loading') : t('statement.downloadPdf')}
                        </button>
                    </div>
                </header>

                {data?.sharedAccount && (
                    <p className="mb-6 text-sm text-ink-muted italic">
                        {t('dashboard.sharedAccount', { n: data.sharedAccount.covers })}
                    </p>
                )}

                {loading && !data && <div className="card h-64 animate-pulse" />}
                {err && <div className="card text-ember-deep">{err}</div>}

                {data && !data.multiCurrency && (
                    <SingleCurrencyTable data={data} lng={i18n.language} />
                )}

                {data && data.multiCurrency && (
                    <div className="space-y-6 sm:space-y-8">
                        {Array.from(groups.entries()).map(([crncID, lines]) => {
                            const op = data.opening.find((o) => o.crncID === crncID);
                            return (
                                <CurrencyGroupTable
                                    key={crncID ?? 'unknown'}
                                    currency={lines[0]?.currency ?? op?.currency ?? '—'}
                                    opening={op?.balance ?? 0}
                                    hasOpening={!!op}
                                    lines={lines}
                                    lng={i18n.language}
                                />
                            );
                        })}
                    </div>
                )}
            </div>
        </AppShell>
    );
}

// --- Preset toggle group -------------------------------------------------
function PresetGroup({ current, onChange }: { current: Preset; onChange: (p: Preset) => void }) {
    const { t } = useTranslation();
    const items: { k: Preset; label: string }[] = [
        { k: 'last30', label: t('statement.last30') },
        { k: 'last90', label: t('statement.last90') },
        { k: 'ytd',    label: t('statement.ytd') },
        { k: 'all',    label: t('statement.all') },
    ];
    return (
        <div className="flex w-full sm:inline-flex sm:w-auto rounded-sm overflow-hidden border border-slate-line bg-slate-raised" role="tablist">
            {items.map((it) => (
                <button key={it.k}
                        role="tab"
                        aria-selected={it.k === current}
                        onClick={() => onChange(it.k)}
                        className={`flex-1 sm:flex-initial px-2 sm:px-3 py-2 text-[10px] sm:text-xs uppercase tracking-[0.18em] sm:tracking-widest2 transition-colors whitespace-nowrap
                                    ${it.k === current ? 'bg-ember text-cream' : 'text-ink-muted hover:text-ink-on hover:bg-slate-fog'}`}>
                    {it.label}
                </button>
            ))}
        </div>
    );
}

// --- Tables --------------------------------------------------------------
// The card is rendered with no horizontal padding so the table-scroll region
// can fill its full width edge-to-edge; summary rows and headings re-apply
// padding locally so they don't bleed into the scroll affordance.

// Single-currency rendering: same hero treatment as the multi-currency block
// but without the currency-code header (it's already in the page subtitle).
function SingleCurrencyTable({ data, lng }: { data: StatementResponse; lng: string }) {
    const opening = data.opening[0]?.balance ?? 0;
    const currency = data.unit.currency || '—';
    const closing = data.lines.length > 0 ? data.lines[data.lines.length - 1].balance : opening;
    const hasOpening = data.opening.length > 0;
    return (
        <CurrencyBlock
            currency={currency}
            showCurrencyHeading={false}
            opening={opening}
            hasOpening={hasOpening}
            closing={closing}
            lines={data.lines}
            multiCurrency={false}
            lng={lng}
        />
    );
}

function CurrencyGroupTable({ currency, opening, hasOpening, lines, lng }: {
    currency: string; opening: number; hasOpening: boolean; lines: StatementLine[]; lng: string;
}) {
    const closing = lines.length > 0 ? lines[lines.length - 1].balance : opening;
    return (
        <CurrencyBlock
            currency={currency}
            showCurrencyHeading={true}
            opening={opening}
            hasOpening={hasOpening}
            closing={closing}
            lines={lines}
            multiCurrency={true}
            lng={lng}
        />
    );
}

// One self-contained currency block. Layout (mobile and desktop alike):
//
//     [optional: currency code, large]
//     CLOSING BALANCE          (eyebrow)
//     -321,018.30 EGP           (hero — matches Dashboard balance-card typography)
//     opening: -0.00 · 27 transactions   (contextual sub-line)
//     ──── TRANSACTIONS ──── separator
//     [scrollable table]
//
// The hero closing-balance answers "where am I?" before any scrolling is
// required; the table is supporting evidence beneath.
function CurrencyBlock({
    currency, showCurrencyHeading, opening, hasOpening, closing, lines, multiCurrency, lng,
}: {
    currency: string;
    showCurrencyHeading: boolean;
    opening: number;
    hasOpening: boolean;
    closing: number;
    lines: StatementLine[];
    multiCurrency: boolean;
    lng: string;
}) {
    const { t } = useTranslation();
    return (
        <section className="card !p-0 overflow-hidden relative">
            {/* corner ember mark — same accent the dashboard balance card uses */}
            <span aria-hidden="true" className="absolute top-0 start-0 w-12 h-px bg-ember" />
            <span aria-hidden="true" className="absolute top-0 start-0 w-px h-12 bg-ember" />

            {/* Hero zone */}
            <div className="px-5 sm:px-7 pt-7 pb-5">
                {showCurrencyHeading && (
                    <p className="font-display text-ink-on text-2xl sm:text-3xl tracking-tight mb-4">
                        {currency}
                    </p>
                )}

                <p className="eyebrow text-ember">{t('statement.closing')}</p>
                <p className="balance-number accounting text-ink-on mt-2"
                   style={{ fontSize: 'clamp(2.25rem, 8vw, 3.5rem)' }}>
                    {fmtAccounting(closing)}
                    {!showCurrencyHeading && (
                        <span className="ms-3 font-display text-ember text-2xl">{currency}</span>
                    )}
                </p>

                <p className="text-ink-muted text-sm mt-3 font-body">
                    {hasOpening && (
                        <>
                            <span className="text-ink-dim">{t('statement.opening')}: </span>
                            <span className="accounting text-ink-on">{fmtAccounting(opening)}</span>
                        </>
                    )}
                    {hasOpening && lines.length > 0 && <span className="text-ink-dim mx-2">·</span>}
                    {lines.length > 0 && (
                        <span className="text-ink-on">{lines.length} {lines.length === 1 ? 'transaction' : 'transactions'}</span>
                    )}
                </p>
            </div>

            {/* Section separator */}
            <div className="px-5 sm:px-7">
                <div className="flex items-center gap-3 pb-3">
                    <span className="text-[10px] uppercase tracking-widest2 text-ink-dim">
                        {t('statement.title')}
                    </span>
                    <span aria-hidden="true" className="flex-1 h-px bg-slate-line/60" />
                </div>
            </div>

            {/* Scrollable ledger or empty-state */}
            {lines.length === 0 ? (
                <p className="text-ink-muted text-center py-10 italic px-5">{t('statement.empty')}</p>
            ) : (
                <ScrollableLedger lines={lines} multiCurrency={multiCurrency} lng={lng} className="pb-5" />
            )}
        </section>
    );
}

// Horizontally-scrollable ledger table. The scroll affordance is contained
// inside the card so the rest of the page (page header, summary rows) stays
// at the viewport width on mobile. min-w-[640px] preserves column alignment
// on narrow screens — users swipe horizontally to read all six columns.
function ScrollableLedger({ lines, multiCurrency, lng, className = '' }: {
    lines: StatementLine[]; multiCurrency: boolean; lng: string; className?: string;
}) {
    const { t } = useTranslation();
    return (
        <div className={`relative ${className}`} data-testid="ledger-scroll">
            <div className="overflow-x-auto overscroll-x-contain">
                <table className="w-full min-w-[640px] border-collapse">
                    <thead>
                        <tr className="border-y border-slate-line text-[10px] uppercase tracking-widest2 text-ink-dim bg-slate-base/40">
                            <th className="text-start font-medium py-3 ps-4 sm:ps-7 pe-2 w-12">{t('statement.col_num')}</th>
                            <th className="text-start font-medium py-3 pe-3 w-[100px]">{t('statement.col_date')}</th>
                            <th className="text-start font-medium py-3 pe-3">{t('statement.col_desc')}</th>
                            <th className="text-end font-medium py-3 pe-3 w-[100px]">{t('statement.col_dbt')}</th>
                            <th className="text-end font-medium py-3 pe-3 w-[100px]">{t('statement.col_cdt')}</th>
                            <th className="text-end font-medium py-3 pe-4 sm:pe-7 w-[140px]">{t('statement.col_balance')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {lines.map((r, i) => (
                            <tr key={`${r.leID}-${r.ledID}`}
                                className="border-b border-slate-line/40 hover:bg-slate-fog/40 transition-colors">
                                <td className="py-3 ps-4 sm:ps-7 pe-2 text-ink-dim text-sm tabular-nums">{i + 1}</td>
                                <td className="py-3 pe-3 text-ink-on font-body text-sm tabular-nums whitespace-nowrap">{fmtDate(r.dt, lng)}</td>
                                <td className="py-3 pe-3 text-ink-on font-body text-sm">{r.dsc || '—'}</td>
                                <td className="py-3 pe-3 text-end text-ink-muted text-sm accounting whitespace-nowrap">{r.dbt ? fmtMoney(r.dbt) : ''}</td>
                                <td className="py-3 pe-3 text-end text-ink-muted text-sm accounting whitespace-nowrap">{r.cdt ? fmtMoney(r.cdt) : ''}</td>
                                <td className="py-3 pe-4 sm:pe-7 text-end text-ink-on text-sm accounting whitespace-nowrap">
                                    {fmtAccounting(r.balance)}
                                    {multiCurrency && r.currency && (
                                        <span className="text-ink-dim text-[10px] ms-2">{r.currency}</span>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

